import type { CatalogOption, DocumentTypeOption, Stay } from '../../reserva/types'
import { spainId } from '../rules'
import type { GuestData, GuestField } from '../types'
import { Mobbscan } from './mobbscan'
import type { MobbscanDocument } from './mobbscan'
import { getIdentifierTypeId, getSexId, uppercaseToCapitalize } from './mobbscan-utils'

/** `dd/MM/yyyy` → `yyyy-MM-dd` */
const slashDateToIso = (d?: string) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(d ?? '')
  return m ? `${m[3]}-${m[2]}-${m[1]}` : ''
}

/** La app actual da la lectura por fallida si falta alguno de estos (AutoCollectScan.handleOnScanned). */
export function isUnreadable(doc: Partial<MobbscanDocument>): boolean {
  return !doc.name || (!doc.firstSurname && !doc.surname) || !doc.dateOfBirth || !Mobbscan.getDocumentNumber(doc)
}

/**
 * Lectura de MobbScan → parte de viajeros. Portado de AutoCollectScan.fillGuestData de la app actual:
 * provincia por nombre de `region`, municipio por `city` con E8, sexo y tipo de documento por literales,
 * nombres capitalizados, número de soporte solo con DNI español y `ministerioInterior`.
 */
export async function mobbscanDocumentToGuest(
  doc: Partial<MobbscanDocument>,
  { stay, documentType, current, getMunicipalities }: { stay: Pick<Stay, 'registration'>; documentType: DocumentTypeOption; current: GuestData; getMunicipalities: (provinceId: string) => Promise<CatalogOption[]> },
): Promise<{ guest: GuestData; doubtful: GuestField[] }> {
  const { catalogs, rules } = stay.registration
  const isSpain = current.countryId !== '' && current.countryId === spainId(stay)
  const province = catalogs.provinces.find((p) => p.name.toLowerCase() === doc.region?.toLowerCase())
  let municipalityId = ''
  if (province) {
    try {
      const list = await getMunicipalities(province.id)
      municipalityId = list.find((m) => m.value.toLowerCase() === doc.city?.toLowerCase())?.id ?? ''
    } catch {
      municipalityId = ''
    }
  }
  const askSupport = isSpain && documentType.scanCode === 'ESP' && rules.ministerioInterior
  const guest: GuestData = {
    ...current,
    bornDate: slashDateToIso(doc.dateOfBirth) || current.bornDate,
    identifier: Mobbscan.getDocumentNumber(doc) ?? current.identifier,
    identifierTypeId: getIdentifierTypeId(documentType.scanType, catalogs.identifierTypes) || current.identifierTypeId,
    name: uppercaseToCapitalize(doc.name) || current.name,
    surname1: uppercaseToCapitalize(doc.firstSurname ?? doc.surname) || current.surname1,
    surname2: uppercaseToCapitalize(doc.secondSurname) || current.surname2,
    sexId: getSexId(doc.gender, catalogs.sexes) || current.sexId,
    provinceId: province?.id ?? current.provinceId,
    supportNumber: askSupport ? (doc.documentNumber ?? '') : current.supportNumber,
    ...(rules.ministerioInterior ? { municipalityId: municipalityId || current.municipalityId, address: doc.address ?? current.address } : {}),
  }
  const doubtful: GuestField[] = []
  if (askSupport && !guest.supportNumber) doubtful.push('supportNumber')
  if (isSpain && !guest.surname2) doubtful.push('surname2')
  if (!guest.sexId) doubtful.push('sexId')
  return { guest, doubtful }
}
